import React from 'react';

import type { UUID } from '@aline/common';
import { ListItemText, Typography } from '@mui/material';
import type { AccountRoleOption } from 'src/components/AccountsRolesAutoComplete';
import { getIsNewRoleOption, getIsNewUserOption } from 'src/components/AccountsRolesAutoComplete';
import { BluePlusIcon } from 'src/components/icons/BluePlusIcon';
import { AlineAvatar } from 'src/components/index';
import { getEntityDisplayDetails } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  optionContainer: {
    alignItems: 'center',
    display: 'flex',
    width: '100%',
  },
  optionText: {
    flexGrow: 1,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(0.5),
  },
  secondaryText: {
    color: theme.appColors.secondaryType,
    fontFamily: theme.typography.fontFamily,
    fontSize: 12,
  },
}));

interface UserAutoCompleteOptionProps {
  option: AccountRoleOption;
  highlightedOptionId?: UUID;
}

export function UserAutoCompleteOption({
  highlightedOptionId,
  option,
}: UserAutoCompleteOptionProps): JSX.Element {
  const { classes } = useStyles();
  const isNewUserOption = getIsNewUserOption(option);
  const {
    avatarColor,
    primary: primaryText,
    secondary: secondaryText,
  } = getEntityDisplayDetails(option);
  const dataCy =
    option.__typename === 'User'
      ? 'user-option'
      : option.__typename === 'Org'
        ? 'org-option'
        : 'role-option';

  return (
    <div className={classes.optionContainer} data-cy={dataCy}>
      <AlineAvatar avatarInfo={option} styles={{ backgroundColor: avatarColor, size: 'md' }} />
      <ListItemText
        className={classes.optionText}
        classes={{ secondary: classes.secondaryText }}
        data-cy="user-option-text"
        primary={primaryText}
        secondary={!isNewUserOption ? secondaryText : undefined}
        slotProps={{
          primary: { align: 'left', noWrap: true, variant: 'body1' },
          secondary: { align: 'left', noWrap: true },
        }}
      />
      <Action highlightedOptionId={highlightedOptionId} option={option} />
    </div>
  );
}

const useActionStyles = makeStyles()(theme => ({
  newRole: {
    ...theme.appTypography.buttonSmall,
    whiteSpace: 'nowrap',
  },
  newUser: {
    ...theme.appTypography.buttonSmall,
    color: theme.appTypography.buttonSmall.color,
    whiteSpace: 'nowrap',
  },
  plusIcon: {
    color: theme.appColors.primary,
  },
}));

function Action({ highlightedOptionId, option }: UserAutoCompleteOptionProps): JSX.Element {
  const { classes } = useActionStyles();
  const isNewUserOption = getIsNewUserOption(option);
  const isNewRoleOption = getIsNewRoleOption(option);
  const highlighted = 'id' in option && option.id === highlightedOptionId;

  if (isNewUserOption)
    return (
      <Typography align="right" className={classes.newUser}>
        Assign email
      </Typography>
    );

  if (isNewRoleOption)
    return (
      <Typography align="right" className={classes.newRole}>
        Create role
      </Typography>
    );

  return (
    <BluePlusIcon
      className={classes.plusIcon}
      style={{ visibility: highlighted ? 'visible' : 'hidden' }}
    />
  );
}
